import {
    LayoutDashboard,
    Package,
    ShoppingCart,
    Users,
    Boxes,
    Settings,
    Store,
    Calculator,
    AlertCircle,
    Crown,
    Calendar
} from "lucide-react"
import type React from "react"

export interface SubLink {
    label: string
    href: string
    allowedRoles?: string[] // Adding allowedRoles to SubLink
    isPro?: boolean // Add isPro property for individual sublinks
    isClickable?: boolean // Add isClickable property for individual sublinks
}

export interface Links {
    label: string
    href?: string
    icon: React.ReactNode
    sublinks?: SubLink[]
    isSection?: boolean
    allowedRoles?: string[]
    isPro?: boolean // Add isPro property to mark premium features
    isClickable?: boolean // Add isClickable property to control if the link can be clicked
}

export const links: Links[] = [
    {
        label: "Accueil",
        href: "/admin",
        icon: <LayoutDashboard className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        allowedRoles: ["admin", "manager"],
        isClickable: true,
    },
    {
        label: "Mon Catalogue",
        icon: <Package className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        isSection: true,
        allowedRoles: ["admin", "manager"],
        isClickable: true,
        sublinks: [
            {
                label: "Produits",
                href: "/admin/entities/products",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Categorie",
                href: "/admin/entities/category",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Sous Categorie",
                href: "/admin/entities/subcategory",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Marques",
                href: "/admin/entities/brand",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Livraisons",
                href: "/admin/entities/shipping",
                allowedRoles: ["admin", "manager"],
                isClickable: true, 
                isPro: true // This specific sublink is Pro
            },
            {
                label: "Clients",
                href: "/admin/entities/clients",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
        ],
    },
    {
        label: "Ma caisse",
        icon: <ShoppingCart className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        isSection: true,
        allowedRoles: ["admin", "manager"],
        isClickable: true,
        sublinks: [
            {
                label: "Point de vente",
                href: "/admin/cash-register/pos",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Remboursement",
                href: "/admin/cash-register/refunds",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Ventes",
                href: "/admin/cash-register/sales",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
        ],
    },
    {
        label: "Réservations",
        href: "/admin/reservations",
        icon: <Calendar className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        allowedRoles: ["admin", "manager"],
        isClickable: true,
    },
    {
        label: "E-Boutique",
        icon: <Store className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        isSection: true,
        allowedRoles: ["admin", "manager"],
        isPro: true, // Mark the entire section as Pro feature
        isClickable: true, // Make it not clickable
        sublinks: [
            {
                label: "Commandes en ligne",
                href: "/admin/store/orders",
                allowedRoles: ["admin", "manager"],
                isPro: true, // This sublink is also Pro
                isClickable: true, // Make it clickable now
            },
        ],
    },
    {
        label: "Mon stock",
        icon: <Boxes className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        isSection: true,
        allowedRoles: ["admin", "manager"],
        isClickable: true,
        sublinks: [
            {
                label: "Arrivages",
                href: "/admin/stock/management",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Inventaire",
                href: "/admin/stock/inventory",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
            {
                label: "Fournisseurs",
                href: "/admin/stock/suppliers",
                allowedRoles: ["admin", "manager"],
                isClickable: true,
            },
        ],
    },
    {
        label: "Données finances",
        icon: <Calculator className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        isSection: true,
        allowedRoles: ["admin","manager"],
        isClickable: true,
        sublinks: [
            {
                label: "Données",
                href: "/admin/comptability/statistics",
                allowedRoles: ["admin","manager"], // Only admin can see statistics
                isClickable: true,
            },
            {
                label: "Compta",
                href: "/admin/comptability/accounting",
                allowedRoles: ["admin","manager"], // Both admin and comptable can see accounting
                isClickable: true,
            },
        ],
    },
    {
        label: "Données finances",
        icon: <Calculator className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        isSection: true,
        allowedRoles: ["comptable"],
        isClickable: true,
        sublinks: [
            {
                label: "Données",
                href: "/comptable",
                allowedRoles: ["comptable"], // Only admin can see statistics
                isClickable: true,
            },
            {
                label: "Compta",
                href: "/comptable/accounting",
                allowedRoles: [ "comptable" ], // Both admin and comptable can see accounting
                isClickable: true,
            },
        ],
    },
    {
        label: "Utilisateurs",
        href: "/admin/users",
        icon: <Users className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        allowedRoles: ["admin"],
        isClickable: true,
    },
    {
        label: "Problèmes",
        icon: <AlertCircle className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        isSection: true,
        allowedRoles: ["admin", "manager"],
        isClickable: true,
        sublinks: [
            {
                label: "Problèmes",
                href: "/admin/issues/list",
                allowedRoles: ["admin"], // Only admin can see list of problems
                isClickable: true,
            },
            {
                label: "Support",
                href: "/admin/issues",
                allowedRoles: ["admin", "manager"], // Both can access support
                isClickable: true,
            },
        ],
    },
    {
        label: "Paramètres",
        href: "/admin/settings",
        icon: <Settings className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
        allowedRoles: ["admin"],
        isClickable: true,
    },
]